import { UserPlus } from "lucide-react-native";
import React, { memo } from "react";

import MobileFilter from "@/components/organisms/MobileFilter";
import PopoverMenu from "@/components/organisms/PopoverMenu";
import Searchbar from "@/components/molecules/Searchbar";
import SelectionSortPopover from "@/components/organisms/SelectionSortPopover";
import Share from "@/components/organisms/Share";
import SkeletonWatchlistView from "@/components/organisms/SkeletonWatchlist";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import {
  WatchlistUserFragment,
  WatchlistUserWatchlistFragment,
  WatchlistWatchlistMetaFragment,
} from "@/generated/graphql";

/**
 *
 */
interface Props {
  watchlists?: WatchlistWatchlistMetaFragment[];
  selectedWatchlist?: WatchlistWatchlistMetaFragment | null;
  isShowPopoverFilter?: boolean;
  onSearchFocus?: () => void;
  onSelectWatchlist?: (value: string) => void;
  onOpenMyRoster?: () => void;
  onCallbackOpen?: () => void;
  onClickableShare?: () => void;
  loading?: boolean;
  collaboratorCount: number;
  isShowWatchList: boolean;
  isMyWatchList: boolean;
  watchListOwnerName: string;
  collaborators: WatchlistUserFragment[];
  userWatchLists: WatchlistUserWatchlistFragment[];
  isSmallScreen: boolean;
  isShowHeaderSearch: boolean | undefined;
}

/**
 * View component for the watchlist header UI presentation
 * @param root0 - Component props
 * @param root0.watchlists - Array of available watchlists for selection
 * @param root0.userWatchLists - User-watchlist relationships including permissions
 * @param root0.isShowWatchList - Whether to display the watchlist selector UI
 * @param root0.collaboratorCount - Number of collaborators on the selected watchlist
 * @param root0.isMyWatchList - Whether the current user owns the selected watchlist
 * @param root0.collaborators - Array of users collaborating on this watchlist
 * @param root0.selectedWatchlist - Currently selected watchlist to display
 * @param root0.watchListOwnerName - Full name of the watchlist owner for display
 * @param root0.isShowPopoverFilter - Whether to show the filter popover button
 * @param root0.isShowHeaderSearch - Whether to display the search bar
 * @param root0.onSearchFocus - Callback invoked when search input receives focus
 * @param root0.onSelectWatchlist - Callback invoked when user selects a watchlist with watchlist ID
 * @param root0.onOpenMyRoster - Callback invoked when user clicks the My Roster button
 * @param root0.onCallbackOpen - Callback invoked when user opens the watchlist menu
 * @param root0.onClickableShare - Callback invoked when user clicks the share button
 * @param root0.loading - Whether watchlist data is currently loading
 * @param root0.isSmallScreen - Whether the screen size is small (mobile)
 */
const HeaderWatchListView = ({
  watchlists,
  userWatchLists,
  isShowWatchList,
  collaboratorCount,
  isMyWatchList,
  collaborators,
  selectedWatchlist,
  watchListOwnerName,
  isShowPopoverFilter,
  isShowHeaderSearch = true,
  onSearchFocus,
  onSelectWatchlist,
  onOpenMyRoster,
  onCallbackOpen,
  onClickableShare,
  loading,
  isSmallScreen,
}: Props) => (
  <Stack>
    {isShowHeaderSearch && (
      <Box className="px-3">
        <Searchbar onFocus={onSearchFocus} />
        {/* SE-5548: one filter implementation everywhere — the redesigned
            MobileFilterSheet presents as a bottom sheet on small screens and
            as a centered modal on desktop/iPad (replaces the legacy
            ModalFilter side panel). */}
        <MobileFilter presentation={isSmallScreen ? "actionsheet" : "modal"} />
      </Box>
    )}

    {loading ? (
      <SkeletonWatchlistView bgColor="bg-surface-muted" />
    ) : (
      <Stack
        direction="horizontal"
        className={`${isShowWatchList ? "justify-between" : "justify-end"} items-center`}
      >
        {isShowWatchList && (
          <Stack
            direction="horizontal"
            className={`${!isSmallScreen ? "pl-2" : ""} min-w-0 flex-1 shrink overflow-hidden`}
          >
            {isShowWatchList && (
              <Stack
                direction="horizontal"
                className={`min-w-0 shrink overflow-hidden ${collaboratorCount > 0 ? "flex-col items-start" : "items-center gap-3 "}`}
              >
                <PopoverMenu
                  selectedWatchlist={selectedWatchlist}
                  onSelect={onSelectWatchlist!}
                  onCallbackOpen={onCallbackOpen}
                  onOpenMyRoster={onOpenMyRoster}
                  watchlists={watchlists ?? []}
                />
                {collaboratorCount <= 0 && !!selectedWatchlist && (
                  <Button
                    className="justify-start"
                    variant="link"
                    accessibilityLabel="Share watchlist"
                    onPress={onClickableShare}
                    icon={UserPlus}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
        {!!selectedWatchlist && (
          <Share
            watchlist={selectedWatchlist}
            isMyWatchList={isMyWatchList}
            collaborators={collaborators}
            userWatchLists={userWatchLists}
          />
        )}
        {isShowPopoverFilter && (
          <Stack
            direction="horizontal"
            className="min-w-0 shrink overflow-hidden"
          >
            <SelectionSortPopover type="watchlist" />
          </Stack>
        )}
      </Stack>
    )}
    {!!selectedWatchlist && isShowWatchList && collaboratorCount > 0 && (
      <Pressable
        onPress={onClickableShare}
        className={!isSmallScreen ? "pl-2" : ""}
      >
        <Text variant="body">
          {`${isMyWatchList ? "Me" : watchListOwnerName} and ${collaboratorCount} other${collaboratorCount > 1 ? "s" : ""}`}
        </Text>
      </Pressable>
    )}
  </Stack>
);

HeaderWatchListView.displayName = "HeaderWatchListView";

export default memo(HeaderWatchListView);
